import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    InputGroup,
    InputGroupText,
    Input,
    Button,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Pagination,
    PaginationItem,
    PaginationLink,
    Spinner,
    Alert,
    FormGroup,
    Label,
    FormFeedback,
} from "reactstrap";
import { FiSearch, FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import "./JobListings.css";
import { getAllJobs, updateJob, deleteJob } from "../../services/api";
import { toast } from 'react-toastify';

// For demo – in real app this would come from API
function JobListings() {
    const [searchTerm, setSearchTerm] = useState("");
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [deleteModal, setDeleteModal] = useState({ open: false, jobId: null });

    // ── Edit Modal States ──
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editJob, setEditJob] = useState(null);
    const [editErrors, setEditErrors] = useState({});
    const [editLoading, setEditLoading] = useState(false);
    const navigate = useNavigate();

    const JOBS_PER_PAGE = 5;



    useEffect(() => {
        const fetchJobs = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getAllJobs();
                setJobs(data); // array of jobs from your API response
            } catch (err) {
                setError(err.message || "Failed to load jobs. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchJobs();
    }, []);


    // Filter jobs
    const filteredJobs = jobs.filter(
        (job) =>
            job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            job.department.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Pagination logic
    const totalPages = Math.ceil(filteredJobs.length / JOBS_PER_PAGE);
    const startIndex = (currentPage - 1) * JOBS_PER_PAGE;
    const paginatedJobs = filteredJobs.slice(startIndex, startIndex + JOBS_PER_PAGE);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    // Delete logic
    const toggleDeleteModal = (job = null) => {
        setDeleteModal({ open: !deleteModal.open, job });
    };

    const handleDelete = async () => {
        if (!deleteModal.job?.id) return;

        try {
            // Call real DELETE API
            await deleteJob(deleteModal.job.id);

            // Refresh the full list from server
            const refreshed = await getAllJobs();
            setJobs(refreshed);

            toast.success(`"${deleteModal.job.title}" deleted successfully`, {
                position: "top-right",
                autoClose: 4000,
            });
        } catch (err) {
            toast.error(err.message || "Failed to delete job. Please try again.", {
                position: "top-right",
                autoClose: 5000,
            });
        } finally {
            toggleDeleteModal(); // close modal in all cases
        }
    };

    // Edit – navigate to edit page (you can pass job id via state or url)
    // ── Edit Handlers ──
    const openEditModal = (job) => {
        setEditJob({
            id: job.id,
            title: job.title || "",
            department: job.department?.trim() || "",
            location: job.location?.trim() || "",
            employmentType: job.employmentType || "",
            experienceRequired: job.experienceRequired || "",
            salaryRange: job.salaryRange || "",
            applicationDeadline: job.applicationDeadline || "",
            status: job.status || "Draft",
            requiredSkills: job.requiredSkills || "",
            description: job.description || "",
            keyResponsibilities:
                Array.isArray(job.keyResponsibilities) && job.keyResponsibilities.length > 0
                    ? job.keyResponsibilities
                    : [""],
        });
        setEditErrors({});
        setEditModalOpen(true);
    };

    // Key Responsibilities handlers for Edit Modal
    const handleEditResponsibilityChange = (index, value) => {
        const updated = [...editJob.keyResponsibilities];
        updated[index] = value;
        setEditJob((prev) => ({ ...prev, keyResponsibilities: updated }));
    };

    const addEditResponsibility = () => {
        setEditJob((prev) => ({
            ...prev,
            keyResponsibilities: [...(prev.keyResponsibilities || []), ""],
        }));
    };

    const removeEditResponsibility = (index) => {
        if (!editJob.keyResponsibilities || editJob.keyResponsibilities.length <= 1) return;
        const updated = editJob.keyResponsibilities.filter((_, i) => i !== index);
        setEditJob((prev) => ({ ...prev, keyResponsibilities: updated }));
    };

   const validateEditForm = () => {
  const errs = {};

  if (!editJob.title?.trim()) errs.title = "Job Title is required";
  if (!editJob.department?.trim()) errs.department = "Department is required";
  if (!editJob.location?.trim()) errs.location = "Location is required";
  if (!editJob.employmentType) errs.employmentType = "Employment Type is required";
  if (!editJob.experienceRequired?.trim())
    errs.experienceRequired = "Experience is required";
  if (!editJob.applicationDeadline)
    errs.applicationDeadline = "Deadline is required";
  if (!editJob.requiredSkills?.trim()) errs.requiredSkills = "Skills are required";
  if (!editJob.description?.trim()) errs.description = "Description is required";

  // Future date validation
  if (editJob.applicationDeadline) {
    const selectedDate = new Date(editJob.applicationDeadline);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // compare only date

    if (selectedDate < today) {
      errs.applicationDeadline = "Application deadline must be a future date";
    }
  }

  setEditErrors(errs);
  return Object.keys(errs).length === 0;
};

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditJob((prev) => ({ ...prev, [name]: value }));
        if (editErrors[name]) {
            setEditErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleSaveEdit = async () => {
        if (!validateEditForm()) return;

        setEditLoading(true);
        try {
            // Clean empty responsibilities
            const cleanedResponsibilities = (editJob.keyResponsibilities || [])
                .map((r) => r.trim())
                .filter(Boolean);

            const payload = {
                title: editJob.title,
                department: editJob.department,
                location: editJob.location,
                employmentType: editJob.employmentType,
                experienceRequired: editJob.experienceRequired,
                salaryRange: editJob.salaryRange || undefined,
                applicationDeadline: editJob.applicationDeadline,
                status: editJob.status,
                requiredSkills: editJob.requiredSkills,
                description: editJob.description,
                keyResponsibilities: cleanedResponsibilities, // ← added
            };

            await updateJob(editJob.id, payload);

            // Refresh list
            const updatedJobs = await getAllJobs();
            setJobs(updatedJobs);

            setEditModalOpen(false);
            toast.success("Job updated successfully!", {
                position: "top-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "light",
            });
        } catch (err) {
            toast.error(err.message || "Failed to update job. Please try again.", {
                position: "top-right",
                autoClose: 5000,
            });
        } finally {
            setEditLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <Spinner color="primary" />
                <span className="ms-3">Loading jobs...</span>
            </div>
        );
    }

    return (
        <div className="job-listings-page">
            <Container fluid className="job-listings-container py-2 py-lg-3">
                {/* Header */}
                <Row className="align-items-center justify-content-between mb-4 mb-md-5">
                    <Col>
                        <h1 className="jl-title">Job Listings</h1>
                        <p className="jl-subtitle">Manage all your job postings</p>
                    </Col>
                    <Col xs="auto">
                        <Button
                            color="success"
                            className="jl-add-btn"
                            onClick={() => navigate("/admin/careers")}
                        >
                            <FiPlus className="me-2" size={18} />
                            Add New Job
                        </Button>
                    </Col>
                </Row>
                {error && (
                    <Alert color="danger" className="mb-4">
                        {error}
                    </Alert>
                )}
                {/* Search */}
                <Row className="mb-5">
                    <Col md={6} lg={5} xl={4}>
                        <InputGroup className="jl-search-group">
                            <InputGroupText className="jl-search-icon">
                                <FiSearch size={18} />
                            </InputGroupText>
                            <Input
                                type="text"
                                placeholder="Search jobs..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1); // reset to page 1 on new search
                                }}
                                className="jl-search-input"
                            />
                        </InputGroup>
                    </Col>
                </Row>

                {/* Table */}
                <Card className="jl-table-card shadow border-0 overflow-hidden">
                    <CardBody className="p-0">
                        <div className="table-responsive">
                            <Table className="jl-jobs-table mb-0" hover borderless>
                                <thead>
                                    <tr className="jl-table-header">
                                        <th>JOB TITLE</th>
                                        <th>DEPARTMENT</th>
                                        <th>LOCATION</th>
                                        <th>TYPE</th>
                                        <th>STATUS</th>
                                        <th>POSTED</th>
                                        <th>EXPERIENCE</th>
                                        <th>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedJobs.map((job) => (
                                        <tr key={job.id} className="jl-table-row">
                                            <td className="jl-job-title">{job.title}</td>
                                            <td className="jl-cell">{job.department?.trim()}</td>
                                            <td className="jl-cell">{job.location?.trim()}</td>
                                            <td className="jl-cell">{job.employmentType}</td>
                                            <td>
                                                <span
                                                    pill
                                                    className={`jl-status-badge jl-status-${job.status.toLowerCase()}`}
                                                >
                                                    {job.status}
                                                </span>
                                            </td>
                                            <td className="jl-cell jl-date">{job.createdAt
                                                ? new Date(job.createdAt).toLocaleDateString("en-IN")
                                                : "—"}</td>
                                            <td className="jl-cell jl-applicants">{job.experienceRequired}</td>
                                            <td>
                                                <div className="jl-actions">
                                                    <button
                                                        className="jl-action-btn jl-edit-btn"
                                                        title="Edit Job"
                                                        onClick={() => openEditModal(job)}
                                                    >
                                                        <FiEdit size={16} />
                                                    </button>
                                                    <button
                                                        className="jl-action-btn jl-delete-btn"
                                                        title="Delete Job"
                                                        onClick={() => toggleDeleteModal(job)}
                                                    >
                                                        <FiTrash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {filteredJobs.length === 0 && (
                            <div className="jl-no-results text-center py-5">
                                No jobs found matching your search.
                            </div>
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="jl-pagination-wrapper p-4 d-flex justify-content-center">
                                <Pagination size="sm">
                                    <PaginationItem disabled={currentPage === 1}>
                                        <PaginationLink previous onClick={() => goToPage(currentPage - 1)}>
                                            Previous
                                        </PaginationLink>
                                    </PaginationItem>

                                    {[...Array(totalPages)].map((_, i) => {
                                        const page = i + 1;
                                        return (
                                            <PaginationItem active={page === currentPage} key={page}>
                                                <PaginationLink onClick={() => goToPage(page)}>
                                                    {page}
                                                </PaginationLink>
                                            </PaginationItem>
                                        );
                                    })}

                                    <PaginationItem disabled={currentPage === totalPages}>
                                        <PaginationLink next onClick={() => goToPage(currentPage + 1)}>
                                            Next
                                        </PaginationLink>
                                    </PaginationItem>
                                </Pagination>
                            </div>
                        )}
                    </CardBody>
                </Card>
                {/* ── Edit Modal ── */}
                <Modal
                    isOpen={editModalOpen}
                    toggle={() => setEditModalOpen(false)}
                    size="lg"
                    centered
                    scrollable
                    className="edit-job-modal-wrapper"
                >
                    <ModalHeader
                        toggle={() => setEditModalOpen(false)}
                        className="edit-job-modal-header"
                    >
                        Edit Job Posting
                    </ModalHeader>

                    <ModalBody className="edit-job-modal-body">
                        {editJob && (
                            <div className="edit-job-modal-form-container">
                                <Row form>
                                    <Col md={6}>
                                        <FormGroup className="edit-job-form-group">
                                            <Label className="edit-job-label">
                                                Job Title <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                type="text"
                                                name="title"
                                                value={editJob.title}
                                                onChange={handleEditChange}
                                                invalid={!!editErrors.title}
                                                className="edit-job-input"
                                            />
                                            <FormFeedback className="edit-job-feedback">
                                                {editErrors.title}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup className="edit-job-form-group">
                                            <Label className="edit-job-label">
                                                Department <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                type="text"
                                                name="department"
                                                value={editJob.department}
                                                onChange={handleEditChange}
                                                invalid={!!editErrors.department}
                                                className="edit-job-input"
                                            />
                                            <FormFeedback className="edit-job-feedback">
                                                {editErrors.department}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row form>
                                    <Col md={6}>
                                        <FormGroup className="edit-job-form-group">
                                            <Label className="edit-job-label">
                                                Location <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                type="text"
                                                name="location"
                                                value={editJob.location}
                                                onChange={handleEditChange}
                                                invalid={!!editErrors.location}
                                                className="edit-job-input"
                                            />
                                            <FormFeedback className="edit-job-feedback">
                                                {editErrors.location}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup className="edit-job-form-group">
                                            <Label className="edit-job-label">
                                                Employment Type <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                type="select"
                                                name="employmentType"
                                                value={editJob.employmentType}
                                                onChange={handleEditChange}
                                                invalid={!!editErrors.employmentType}
                                                className="edit-job-select"
                                            >
                                                <option value="">Select type</option>
                                                <option>Full-time</option>
                                                <option>Part-time</option>
                                                <option>Contract</option>
                                                <option>Internship</option>
                                            </Input>
                                            <FormFeedback className="edit-job-feedback">
                                                {editErrors.employmentType}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row form>
                                    <Col md={6}>
                                        <FormGroup className="edit-job-form-group">
                                            <Label className="edit-job-label">
                                                Experience Required <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                type="text"
                                                name="experienceRequired"
                                                value={editJob.experienceRequired}
                                                onChange={handleEditChange}
                                                invalid={!!editErrors.experienceRequired}
                                                className="edit-job-input"
                                            />
                                            <FormFeedback className="edit-job-feedback">
                                                {editErrors.experienceRequired}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup className="edit-job-form-group">
                                            <Label className="edit-job-label">Salary Range</Label>
                                            <Input
                                                type="text"
                                                name="salaryRange"
                                                value={editJob.salaryRange}
                                                onChange={handleEditChange}
                                                placeholder="e.g. ₹12-18 LPA"
                                                className="edit-job-input"
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Row form>
                                    <Col md={6}>
                                        <FormGroup className="edit-job-form-group">
                                            <Label className="edit-job-label">
                                                Application Deadline <span className="text-danger">*</span>
                                            </Label>
                                            <Input
                                                type="date"
                                                name="applicationDeadline"
                                                value={editJob.applicationDeadline}
                                                onChange={handleEditChange}
                                                invalid={!!editErrors.applicationDeadline}
                                                className="edit-job-input"
                                            />
                                            <FormFeedback className="edit-job-feedback">
                                                {editErrors.applicationDeadline}
                                            </FormFeedback>
                                        </FormGroup>
                                    </Col>

                                    <Col md={6}>
                                        <FormGroup className="edit-job-form-group">
                                            <Label className="edit-job-label">Status</Label>
                                            <Input
                                                type="select"
                                                name="status"
                                                value={editJob.status}
                                                onChange={handleEditChange}
                                                className="edit-job-select"
                                            >
                                                <option>Draft</option>
                                                <option>Published</option>
                                                <option>Closed</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <FormGroup className="edit-job-form-group">
                                    <Label className="edit-job-label">
                                        Required Skills <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="text"
                                        name="requiredSkills"
                                        value={editJob.requiredSkills}
                                        onChange={handleEditChange}
                                        placeholder="React, TypeScript, Node.js (comma separated)"
                                        invalid={!!editErrors.requiredSkills}
                                        className="edit-job-input"
                                    />
                                    <FormFeedback className="edit-job-feedback">
                                        {editErrors.requiredSkills}
                                    </FormFeedback>
                                </FormGroup>
                                {/* ========== KEY RESPONSIBILITIES ========== */}
                                <FormGroup className="edit-job-form-group mt-3">
                                    <Label className="edit-job-label">
                                        Key Responsibilities <span className="text-danger">*</span>
                                    </Label>

                                    <div className="edit-job-key-resp-box">
                                        {(editJob.keyResponsibilities || [""]).map((item, index) => (
                                            <div key={index} className="edit-job-key-resp-row">
                                                <Input
                                                    type="text"
                                                    placeholder={`e.g. Responsibility ${index + 1}`}
                                                    value={item}
                                                    onChange={(e) => handleEditResponsibilityChange(index, e.target.value)}
                                                    className="edit-job-key-resp-input"
                                                />
                                                <button
                                                    type="button"
                                                    className="edit-job-key-resp-delete"
                                                    onClick={() => removeEditResponsibility(index)}
                                                    disabled={(editJob.keyResponsibilities || []).length <= 1}
                                                    title="Remove"
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            </div>
                                        ))}

                                        <button
                                            type="button"
                                            className="edit-job-key-resp-add"
                                            onClick={addEditResponsibility}
                                        >
                                            <FiPlus size={16} className="me-1" />
                                            Add Key Responsibility
                                        </button>
                                    </div>
                                </FormGroup>

                                <FormGroup className="edit-job-form-group">
                                    <Label className="edit-job-label">
                                        Job Description <span className="text-danger">*</span>
                                    </Label>
                                    <Input
                                        type="textarea"
                                        name="description"
                                        rows="5"
                                        value={editJob.description}
                                        onChange={handleEditChange}
                                        invalid={!!editErrors.description}
                                        className="edit-job-textarea"
                                    />
                                    <FormFeedback className="edit-job-feedback">
                                        {editErrors.description}
                                    </FormFeedback>
                                </FormGroup>
                            </div>
                        )}
                    </ModalBody>

                    <ModalFooter className="edit-job-modal-footer">
                        <Button
                            color="secondary"
                            outline
                            onClick={() => setEditModalOpen(false)}
                            disabled={editLoading}
                            className="edit-job-btn-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            onClick={handleSaveEdit}
                            disabled={editLoading}
                            className="edit-job-btn-save"
                        >
                            {editLoading ? (
                                <>
                                    <Spinner size="sm" className="me-2" /> Saving...
                                </>
                            ) : (
                                "Save Changes"
                            )}
                        </Button>
                    </ModalFooter>
                </Modal>
                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={deleteModal.open}
                    toggle={() => toggleDeleteModal()}
                    centered
                    className="jl-delete-modal"
                    backdrop="static" // prevents closing by clicking outside
                >
                    <ModalHeader
                        toggle={() => toggleDeleteModal()}
                        className="jl-modal-header border-0 pb-0"
                    >
                        <div className="d-flex align-items-center gap-3">
                            <div className="delete-icon-wrapper">
                                <FiTrash2 size={28} className="delete-icon" />
                            </div>
                            <h5 className="modal-title mb-0">Delete Job Posting?</h5>
                        </div>
                    </ModalHeader>

                    <ModalBody className="jl-modal-body pt-3 pb-4">
                        {/* <p className="text-muted mb-1">
                            Are you sure you want to <strong>permanently delete</strong> this job posting?<br />
                            This action <strong>cannot be undone</strong>.
                        </p> */}

                        {deleteModal.job ? (
                            <>
                                <p>
                                    You are about to <strong>permanently delete</strong> the following job:
                                </p>
                                <div className="delete-job-preview mt-3 mb-3 p-3 bg-light rounded border">
                                    <strong>{deleteModal.job.title}</strong>
                                    <div className="small text-muted mt-1">
                                        {deleteModal.job.department?.trim()} • {deleteModal.job.location?.trim()}
                                    </div>
                                    <div className="small text-muted">
                                        Type: {deleteModal.job.employmentType} • Status: {deleteModal.job.status}
                                    </div>
                                </div>
                                <p className="text-danger fw-bold mb-0">
                                    This action cannot be undone.
                                </p>
                            </>
                        ) : (
                            <p>Are you sure you want to delete this job?</p>
                        )}
                    </ModalBody>

                    <ModalFooter className="jl-modal-footer border-0 pt-0 gap-3">
                        <Button
                            color="light"
                            outline
                            className="jl-cancel-btn px-4"
                            onClick={() => toggleDeleteModal()}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            className="jl-delete-confirm-btn px-4 d-flex align-items-center gap-2"
                            onClick={handleDelete}
                            disabled={!deleteModal.job}
                        >
                            <FiTrash2 size={16} />
                            Delete Job
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
}

export default JobListings;